import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim();

export default function ChangePassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const validate = () => {
    const validationErrors = [];

    if (!newPassword) {
      validationErrors.push("New password is required.");
    } else {
      if (newPassword.length < 8) validationErrors.push("Password must be at least 8 characters.");
      if (!/[A-Z]/.test(newPassword)) validationErrors.push("Password must include at least one uppercase letter.");
      if (!/[a-z]/.test(newPassword)) validationErrors.push("Password must include at least one lowercase letter.");
      if (!/[0-9]/.test(newPassword)) validationErrors.push("Password must include at least one digit.");
      if (!/[\W_]/.test(newPassword)) validationErrors.push("Password must include at least one special character.");
    }

    if (newPassword !== confirmPassword) validationErrors.push("Passwords do not match.");

    setErrors(validationErrors);
    return validationErrors.length === 0;
  };

  const handleChangePassword = async () => {
    if (!validate()) return;

    setLoading(true);
    setErrors([]);
    setSuccessMessage("");

    try {
      const userId = localStorage.getItem("user_id");

      const res = await axios.post(`${BACKEND_URL}/api/email/change-password`, {
        user_id: parseInt(userId, 10),
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      setSuccessMessage(res.data.message || "Password changed successfully!");

      // Clear user-related localStorage items to force fresh login
      localStorage.removeItem("user_id");
      localStorage.removeItem("passwordChangeRequired"); // **Clear the flag here**
      localStorage.removeItem("jwt_token"); // optional, clear token if you want to force login

      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      if (error.response?.data?.detail) {
        setErrors([error.response.data.detail]);
      } else {
        setErrors(["Failed to change password. Please try again."]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        input[type="password"]::-webkit-textfield-decoration-container {
          display: none !important;
        }
        input[type="password"]::-ms-reveal {
          display: none !important;
        }
        input[type="password"]::-moz-reveal {
          display: none !important;
        }
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f3f4f6",
          padding: 20,
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        }}
      >
        <div
          style={{
            width: "400px",
            backgroundColor: "white",
            borderRadius: 12,
            padding: 36,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            boxSizing: "border-box",
            border: "1px solid #e5e7eb",
          }}
        >
          <h1
            style={{
              fontSize: 26,
              fontWeight: "700",
              color: "#1f2937",
              marginBottom: 6,
              textAlign: "center",
            }}
          >
            Change Password
          </h1>
          <p
            style={{
              fontSize: 14,
              color: "#6b7280",
              marginBottom: 28,
              textAlign: "center",
              fontWeight: "500",
            }}
          >
            Please enter a strong new password
          </p>

          {errors.length > 0 && (
            <div
              style={{
                marginBottom: 20,
                backgroundColor: "#fee2e2",
                borderRadius: 8,
                padding: 14,
                color: "#991b1b",
                border: "1px solid #fca5a5",
              }}
            >
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {errors.map((error, idx) => (
                  <li key={idx} style={{ fontSize: 14 }}>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {successMessage && (
            <div
              style={{
                marginBottom: 20,
                backgroundColor: "#d1fae5",
                borderRadius: 8,
                padding: 14,
                color: "#065f46",
                border: "1px solid #34d399",
                fontWeight: "600",
                textAlign: "center",
              }}
            >
              ✅ {successMessage}
              <button
                onClick={() => {
                  // Ensure passwordChangeRequired flag cleared and navigate to login
                  localStorage.removeItem("passwordChangeRequired");
                  navigate("/");
                }}
                style={{
                  marginLeft: 10,
                  background: "none",
                  border: "none",
                  color: "#3b82f6",
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontWeight: "600",
                  fontSize: 14,
                }}
              >
                Go to Login
              </button>
            </div>
          )}

          {!successMessage && (
            <>
              <label
                htmlFor="new-password"
                style={{
                  display: "block",
                  fontWeight: "600",
                  marginBottom: 8,
                  color: "#374151",
                  fontSize: 14,
                }}
              >
                New Password
              </label>
              <div style={{ position: "relative", marginBottom: 24 }}>
                <input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  style={{
                    width: "100%",
                    padding: "12px 48px 12px 16px",
                    fontSize: 16,
                    borderRadius: 8,
                    border: "1.5px solid #d1d5db",
                    outline: "none",
                    boxSizing: "border-box",
                    transition: "border-color 0.2s ease",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#2563eb")}
                  onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  style={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    color: "#6b7280",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    height: 28,
                  }}
                >
                  {showPassword ? (
                    <EyeSlashIcon style={{ width: 20, height: 20 }} />
                  ) : (
                    <EyeIcon style={{ width: 20, height: 20 }} />
                  )}
                </button>
              </div>

              <label
                htmlFor="confirm-password"
                style={{
                  display: "block",
                  fontWeight: "600",
                  marginBottom: 8,
                  color: "#374151",
                  fontSize: 14,
                }}
              >
                Confirm Password
              </label>
              <input
                id="confirm-password"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                autoComplete="new-password"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: 16,
                  borderRadius: 8,
                  border: "1.5px solid #d1d5db",
                  outline: "none",
                  boxSizing: "border-box",
                  transition: "border-color 0.2s ease",
                  marginBottom: 32,
                }}
                onFocus={(e) => (e.target.style.borderColor = "#2563eb")}
                onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
              />

              <button
                disabled={loading}
                onClick={handleChangePassword}
                style={{
                  width: "100%",
                  padding: "14px 0",
                  backgroundColor: loading ? "#93c5fd" : "#2563eb",
                  borderRadius: 8,
                  border: "none",
                  color: "white",
                  fontWeight: "700",
                  fontSize: 16,
                  cursor: loading ? "not-allowed" : "pointer",
                  boxShadow: loading ? "none" : "0 6px 18px rgba(37, 99, 235, 0.5)",
                  transition: "background-color 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  if (!loading) e.target.style.backgroundColor = "#1d4ed8";
                }}
                onMouseLeave={(e) => {
                  if (!loading) e.target.style.backgroundColor = "#2563eb";
                }}
              >
                {loading ? "Changing..." : "Change Password"}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
